import { useCallback, useState, useEffect, useRef } from "react";
import { Search } from "../../../../components/ui/Search";
import { Button } from "@/components/ui/button";
import { createFileRoute } from "@tanstack/react-router";
import { SellerBuyerRootMapping } from "@/interfaces";
import { useNavigate } from "@tanstack/react-router";
import { Route as AddCustomerRoute } from "./add-customer";
import "./customer.css";
import { InfiniteItemRenderer, InfiniteScrollList } from "@/components/common/scroll";
import { useGetCustomerListQuery } from "@/features/customer/api";
import { debounce } from "lodash";

import { appColors } from "@/constants/color";
export const Route = createFileRoute("/(app)/(store)/customer/")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const [searchUser, setSearchUser] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [menuPosition, setMenuPosition] = useState()
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null); // Track active menu
  const limit = 10;
  // Fetching Customer List
  const { data: customers = [], isFetching, isError, refetch, } = useGetCustomerListQuery({
    search: searchUser,
    // buyer:true,
    page: page,
    limit,
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        activeMenuId &&
        !(event.target as Element).closest(".menuButton") &&
        !(event.target as Element).closest(".dropdownMenu")
      ) {
        setActiveMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeMenuId]);

  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchUser(value);
      setPage(1);
    }, 500),
    []
  );

  const handleViewOrders = (customer: SellerBuyerRootMapping) => {
  const filterParams = {
    ...(customer.buyerType === 'User' 
      ? { 'buyer-user': customer.buyerUserId }
      : { 'buyer-store': customer.buyerStoreId || customer.buyerUserId }
    )
  };

  navigate({
    to: "/sales/",
    search: {
      customerFilter: filterParams,
      customerName: customer.buyerName || customer.buyerPhoneNumber
    }
  });
};

  const toggleMenu = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (activeMenuId === id) {
      setActiveMenuId(null);
    } else {
      setActiveMenuId(id);
    }
  };

  // In your customer list screen
  const handleEditCustomer = (customer: SellerBuyerRootMapping) => {
    setActiveMenuId(null);
    navigate({
      to: "/customer/update-customer/$sellerBuyerRouteMappingId",
      params: { sellerBuyerRouteMappingId: customer?._id }
    });
  };

  const getFirstLetter = (name?: string) => {
    return name ? name.charAt(0).toUpperCase() : "";
  };

  const getStatusColors = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return { textColor: appColors.successText, bgColor: appColors.successBg };
      case "pending":
        return { textColor: appColors.statusReturnedText, bgColor: appColors.statusReturnedBg };
      case "inactive":
        return { textColor: appColors.errorText, bgColor: appColors.customerStatusErrorBg };
      default:
        return { textColor: appColors.textGray, bgColor: appColors.panelMutedBg };
    }
  };
  const renderItem: InfiniteItemRenderer<SellerBuyerRootMapping> = (customer) => {
    const name = customer.buyerName || customer.buyerPhoneNumber || "Unknown";
    const phone =
      customer.buyerPhoneNumber || "N/A";
    const type =
      customer.buyerType === "User" ? "Customer" : "Business";
    const status = customer.status || "Active";
    const { textColor, bgColor } = getStatusColors(status);

    return (
      <div key={customer._id} className="customerCard"
        onClick={() => handleViewOrders(customer)} 
        style={{ cursor: 'pointer' }} 
      >
        <div className="customerRow">
          <div
            className="avatar"
            style={{ backgroundColor: appColors.statusProcessingText }}
          >
            {getFirstLetter(name)}
          </div>

          <div className="customerInfo">
            <p className="customerName">{name}</p>
            <p className="customerType">{type}</p>
            <p className="customerPhone">{phone}</p>
          </div>

          <div className="customerActions">
            <div
              className="statusBadge"
              style={{ backgroundColor: bgColor, color: textColor }}
            >
              {status}
            </div>
            <button
              className="menuButton"
              onClick={(e) => toggleMenu(customer._id, e)}
            >
              ⋯
            </button>

            {/* Dropdown menu */}
            {activeMenuId === customer._id && (
              <div
                className="dropdownMenu"
                style={{
                  position: "absolute",
                  right: "0",
                  top: "100%",
                  zIndex: 1000,
                }}
              >
                <button
                  className="menuItem"
                  onClick={() => handleEditCustomer(customer)}
                >
                  Edit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="customerScreen">
      <div className="customerHeader">
        <h1>Customers</h1>
        <Button
          className="addButton"
          onClick={() => navigate({ to: AddCustomerRoute.to })}
        >
          Add
        </Button>
      </div>

      <div className="searchContainer">
        <Search
          placeholder="Search customers..."
          onChange={(e) => handleSearch(e.target.value)}
          className="searchInput"
        />
      </div>

      {isFetching && customers.length === 0 ? (
        <div className="loadingContainer">
          <div className="spinner"></div>
        </div>
      ) : isError ? (
        <div className="emptyContainer">
          <p className="emptyText">Failed to load customers</p>
          <Button onClick={() => refetch()} className="retryButton">
            Retry
          </Button>
        </div>
      ) : customers.length === 0 ? (
        <div className="emptyContainer">
          <p className="emptyText">No customers found</p>
        </div>
      ) : (
        <InfiniteScrollList items={customers} renderItem={renderItem} loadMore={() => setPage(pre => pre + 1)} hasMore={limit * page <= customers.length}
        />
      )}
    </div>)
}
